import React, { useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../components/common/Button'
import { Card } from '../components/common/Card'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { RichTextContent } from '../components/common/RichTextContent'
import { RichTextEditor } from '../components/common/RichTextEditor'
import { TaskViewDialog } from '../components/features/TaskViewDialog'
import { useAuth } from '../context/AuthContext'
import {
  TASK_PRIORITY_COLORS,
  TASK_PRIORITY_LABELS,
  TASK_STATUS_COLORS,
  TASK_STATUS_LABELS,
} from '../lib/config/project-config'
import { canProfileSeeStatus } from '../lib/config/status-config'
import { TaskConcurrencyError, mutations } from '../lib/supabase/mutations'
import { queries } from '../lib/supabase/queries'
import { Task, TaskAttachment } from '../lib/types/database.types'
import { transitionTask } from '../lib/workflow/taskWorkflow'
import { sendTaskAssignedEmailIfNeeded } from '../utils/emailNotifications'
import { formatFileSize } from '../utils/fileSize'
import { formatDate, formatDateTime, formatRelativeTime, stripHtml, truncateText } from '../utils/formatters'
import { getTaskCurrentStatusDueDate, isTaskStatusOverdue } from '../utils/statusDates'

type TesterFailActionType = 'TESTER1_FAILED' | 'TESTER2_FAILED'
type TesterPassActionType = 'TESTER1_PASSED' | 'TESTER2_PASSED'
type SortField = 'title' | 'lane' | 'priority' | 'original_dev' | 'due' | 'attempts' | 'updated'
type SortDirection = 'asc' | 'desc'

type FailModalState = {
  task: Task
  actionType: TesterFailActionType
} | null

const badgeClassName = 'inline-flex rounded-full border px-2.5 py-1 text-xs font-medium'
const taskSortCollator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })

export const TesterQueuePage: React.FC = () => {
  const queryClient = useQueryClient()
  const { user, profile } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<SortField>('due')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [viewTask, setViewTask] = useState<Task | null>(null)
  const [failModalState, setFailModalState] = useState<FailModalState>(null)
  const [failureReason, setFailureReason] = useState('')
  const [failureError, setFailureError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSummary, setActionSummary] = useState<string | null>(null)

  const hasTesterAccess = Boolean(profile?.is_tester || profile?.is_2nd_tester)

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'tester-queue'],
    queryFn: () => queries.getTasks(false),
    enabled: hasTesterAccess,
  })

  const profilesQuery = useQuery({
    queryKey: ['profiles', 'all'],
    queryFn: queries.getAllProfiles,
    enabled: hasTesterAccess,
  })

  const taskNotesQuery = useQuery({
    queryKey: ['task-notes', viewTask?.id],
    queryFn: () => queries.getTaskNotes(viewTask!.id),
    enabled: Boolean(viewTask?.id),
  })

  const taskAttachmentsQuery = useQuery({
    queryKey: ['task-attachments', viewTask?.id],
    queryFn: () => queries.getTaskAttachments(viewTask!.id),
    enabled: Boolean(viewTask?.id),
  })

  const profileDisplayById = useMemo(
    () =>
      new Map((profilesQuery.data || []).map((item) => [item.id, item.full_name || item.email || 'Unknown user'])),
    [profilesQuery.data]
  )

  const queueTasks = useMemo(() => {
    let nextTasks = (tasksQuery.data || []).filter(
      (task) =>
        Boolean(user?.id) &&
        task.assigned_to === user?.id &&
        (task.status === 'in_dev_testing' || task.status === 'in_uat_testing') &&
        canProfileSeeStatus(profile, task.status)
    )

    if (searchQuery.trim()) {
      const normalizedSearch = searchQuery.trim().toLowerCase()
      nextTasks = nextTasks.filter((task) => {
        const originalDeveloperName = task.original_dev_id ? profileDisplayById.get(task.original_dev_id) || '' : ''
        const searchFields = [
          task.title,
          task.related_app || '',
          task.requested_by,
          originalDeveloperName,
          stripHtml(task.description || ''),
        ]

        return searchFields.some((field) => field.toLowerCase().includes(normalizedSearch))
      })
    }

    const directionMultiplier = sortDirection === 'asc' ? 1 : -1
    const compareText = (left?: string | null, right?: string | null) => taskSortCollator.compare(left || '', right || '')
    const compareNumber = (left?: number | null, right?: number | null) => (left || 0) - (right || 0)
    const compareDate = (left?: string | null, right?: string | null, missingValue = Number.MAX_SAFE_INTEGER) => {
      const leftValue = left ? new Date(left).getTime() : missingValue
      const rightValue = right ? new Date(right).getTime() : missingValue
      return leftValue - rightValue
    }

    return [...nextTasks].sort((left, right) => {
      let result = 0

      switch (sortField) {
        case 'title':
          result = compareText(left.title, right.title)
          break
        case 'lane':
          result = compareText(left.status === 'in_dev_testing' ? 'First Test' : 'Second Test', right.status === 'in_dev_testing' ? 'First Test' : 'Second Test')
          break
        case 'priority':
          result = compareText(TASK_PRIORITY_LABELS[left.priority as keyof typeof TASK_PRIORITY_LABELS] || left.priority, TASK_PRIORITY_LABELS[right.priority as keyof typeof TASK_PRIORITY_LABELS] || right.priority)
          break
        case 'original_dev':
          result = compareText(
            left.original_dev_id ? profileDisplayById.get(left.original_dev_id) || 'Unknown user' : 'Not set',
            right.original_dev_id ? profileDisplayById.get(right.original_dev_id) || 'Unknown user' : 'Not set'
          )
          break
        case 'due':
          result = compareDate(getTaskCurrentStatusDueDate(left), getTaskCurrentStatusDueDate(right))
          break
        case 'attempts':
          result = compareNumber(
            left.status === 'in_dev_testing' ? left.dev_test_attempts : left.second_test_attempts,
            right.status === 'in_dev_testing' ? right.dev_test_attempts : right.second_test_attempts
          )
          break
        case 'updated':
          result = compareDate(left.updated_at, right.updated_at, Number.NEGATIVE_INFINITY)
          break
      }

      if (result !== 0) {
        return result * directionMultiplier
      }

      return taskSortCollator.compare(left.id, right.id)
    })
  }, [profile, profileDisplayById, searchQuery, sortDirection, sortField, tasksQuery.data, user?.id])

  const workflowMutation = useMutation({
    mutationFn: async ({
      task,
      actionType,
      failReasonHtml,
    }: {
      task: Task
      actionType: TesterFailActionType | TesterPassActionType
      failReasonHtml?: string
    }) => {
      const transition = transitionTask(task, { type: actionType }, profile, {
        users: profilesQuery.data || [],
      })

      if (!transition.ok) {
        throw new Error(transition.reason)
      }

      const updatedTask = await mutations.updateTaskWithExpectedUpdatedAt(task.id, task.updated_at, transition.updates)

      await sendTaskAssignedEmailIfNeeded(updatedTask, profilesQuery.data || [], task.assigned_to, task.status)

      if (failReasonHtml && user?.id) {
        const actorName = profile?.full_name || profile?.email || 'Tester'
        const laneLabel = actionType === 'TESTER1_FAILED' ? 'First Test' : 'Second Test'
        await mutations.createTaskNote({
          task_id: task.id,
          created_by: user.id,
          content: `<p><strong>${laneLabel} failed</strong> by ${actorName}</p>${failReasonHtml}`,
        })
      }

      return updatedTask
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['tasks', 'kanban'] }),
        queryClient.invalidateQueries({ queryKey: ['task-notes', variables.task.id] }),
      ])
    },
  })

  if (!hasTesterAccess) {
    return <Navigate to="/dashboard" replace />
  }

  const isLoading = tasksQuery.isLoading || profilesQuery.isLoading
  const queryError = tasksQuery.error || profilesQuery.error
  const viewNotes = taskNotesQuery.data || []
  const viewAttachments = taskAttachmentsQuery.data || []

  const openFailModal = (task: Task) => {
    setFailModalState({
      task,
      actionType: task.status === 'in_dev_testing' ? 'TESTER1_FAILED' : 'TESTER2_FAILED',
    })
    setFailureReason('')
    setFailureError(null)
    setActionError(null)
    setActionSummary(null)
  }

  const closeFailModal = () => {
    if (workflowMutation.isPending) {
      return
    }

    setFailModalState(null)
    setFailureReason('')
    setFailureError(null)
  }

  const handlePass = async (task: Task) => {
    setActionError(null)
    setActionSummary(null)

    const actionType: TesterPassActionType = task.status === 'in_dev_testing' ? 'TESTER1_PASSED' : 'TESTER2_PASSED'

    try {
      await workflowMutation.mutateAsync({ task, actionType })
      setActionSummary(
        actionType === 'TESTER1_PASSED'
          ? 'Task passed first testing and moved to Ready for UAT.'
          : 'Task passed second testing and moved to Ready for Prod.'
      )
    } catch (error) {
      if (error instanceof TaskConcurrencyError) {
        setActionError(error.message)
        return
      }

      setActionError(error instanceof Error ? error.message : 'Unable to submit the pass result.')
    }
  }

  const handleFailSubmit = async () => {
    if (!failModalState) {
      return
    }

    const plainText = stripHtml(failureReason)
    if (!plainText) {
      setFailureError('A failure reason is required.')
      return
    }

    setFailureError(null)
    setActionError(null)
    setActionSummary(null)

    try {
      await workflowMutation.mutateAsync({
        task: failModalState.task,
        actionType: failModalState.actionType,
        failReasonHtml: failureReason,
      })
      setActionSummary('Failure recorded and task returned to development.')
      closeFailModal()
    } catch (error) {
      if (error instanceof TaskConcurrencyError) {
        setFailureError(error.message)
        return
      }

      setFailureError(error instanceof Error ? error.message : 'Unable to submit the failure result.')
    }
  }

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }

    setSortField(field)
    setSortDirection(field === 'updated' ? 'desc' : 'asc')
  }

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) {
      return null
    }

    return sortDirection === 'asc' ? '↑' : '↓'
  }

  const openTaskDetails = (task: Task) => {
    setViewTask(task)
  }

  const closeTaskDetails = () => {
    setViewTask(null)
  }

  const handleOpenAttachment = async (attachment: TaskAttachment) => {
    try {
      const accessUrl = await queries.getTaskAttachmentAccessUrl(attachment)
      window.open(accessUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Unable to open attachment.')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Testing Queue</h1>
          <p className="text-sm text-gray-600">
            Review your assigned first-test and second-test work, then pass or fail each item inline.
          </p>
        </div>
        <div className="flex-1 md:max-w-md">
          <input
            type="text"
            placeholder="Search by task, app, requester, developer, or description..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm outline-none focus:border-blue-500"
          />
        </div>
      </div>

      {actionSummary && (
        <Card>
          <p className="rounded-lg border border-emerald-300 bg-emerald-100 p-4 text-sm text-emerald-800">
            {actionSummary}
          </p>
        </Card>
      )}

      {(actionError || queryError) && (
        <Card>
          <p className="rounded-lg border border-red-300 bg-red-100 p-4 text-sm text-red-800">
            {actionError || (queryError instanceof Error ? queryError.message : 'Failed to load tester queue.')}
          </p>
        </Card>
      )}

      <Card header={<h2 className="font-semibold text-gray-900">My Testing Tasks</h2>}>
        {isLoading ? (
          <div className="flex min-h-[280px] items-center justify-center">
            <LoadingSpinner size="lg" text="Loading testing queue..." />
          </div>
        ) : queueTasks.length === 0 ? (
          <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 text-center">
            <p className="text-base font-medium text-gray-900">No testing work is assigned to you right now.</p>
            <p className="text-sm text-gray-500">Tasks in first or second testing will show up here once they are assigned.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-stone-100 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">
                    <button className="rounded px-2 py-1 font-semibold text-gray-700 hover:bg-stone-200" onClick={() => toggleSort('title')}>
                      Task {getSortIndicator('title')}
                    </button>
                  </th>
                  <th className="px-4 py-3">
                    <button className="rounded px-2 py-1 font-semibold text-gray-700 hover:bg-stone-200" onClick={() => toggleSort('lane')}>
                      Lane {getSortIndicator('lane')}
                    </button>
                  </th>
                  <th className="px-4 py-3">
                    <button className="rounded px-2 py-1 font-semibold text-gray-700 hover:bg-stone-200" onClick={() => toggleSort('priority')}>
                      Priority {getSortIndicator('priority')}
                    </button>
                  </th>
                  <th className="px-4 py-3">
                    <button className="rounded px-2 py-1 font-semibold text-gray-700 hover:bg-stone-200" onClick={() => toggleSort('original_dev')}>
                      Original Dev {getSortIndicator('original_dev')}
                    </button>
                  </th>
                  <th className="px-4 py-3">
                    <button className="rounded px-2 py-1 font-semibold text-gray-700 hover:bg-stone-200" onClick={() => toggleSort('due')}>
                      Due {getSortIndicator('due')}
                    </button>
                  </th>
                  <th className="px-4 py-3">
                    <button className="rounded px-2 py-1 font-semibold text-gray-700 hover:bg-stone-200" onClick={() => toggleSort('attempts')}>
                      Attempts {getSortIndicator('attempts')}
                    </button>
                  </th>
                  <th className="px-4 py-3">
                    <button className="rounded px-2 py-1 font-semibold text-gray-700 hover:bg-stone-200" onClick={() => toggleSort('updated')}>
                      Updated {getSortIndicator('updated')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {queueTasks.map((task) => {
                  const statusColor = TASK_STATUS_COLORS[task.status as keyof typeof TASK_STATUS_COLORS] || '#6b7280'
                  const priorityColor = TASK_PRIORITY_COLORS[task.priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280'
                  const dueDate = getTaskCurrentStatusDueDate(task)
                  const laneLabel = task.status === 'in_dev_testing' ? 'First Test' : 'Second Test'
                  const attemptCount = task.status === 'in_dev_testing' ? task.dev_test_attempts : task.second_test_attempts
                  const originalDeveloperName = task.original_dev_id
                    ? profileDisplayById.get(task.original_dev_id) || 'Unknown user'
                    : 'Not set'

                  return (
                    <tr key={task.id} className="align-top hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-gray-900">{task.title}</span>
                            <span className={badgeClassName} style={{ borderColor: statusColor, color: statusColor }}>
                              {TASK_STATUS_LABELS[task.status as keyof typeof TASK_STATUS_LABELS] || task.status}
                            </span>
                            {isTaskStatusOverdue(task) && (
                              <span className="rounded-full border border-red-200 bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-700">
                                Overdue
                              </span>
                            )}
                          </div>
                          <p className="max-w-2xl text-gray-600">
                            {truncateText(stripHtml(task.description || 'No description provided.'), 160)}
                          </p>
                          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                            <span>App: {task.related_app || 'None'}</span>
                            <span>Requested By: {task.requested_by || 'Unknown'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-700">{laneLabel}</td>
                      <td className="px-4 py-4">
                        <span className={badgeClassName} style={{ borderColor: priorityColor, color: priorityColor }}>
                          {TASK_PRIORITY_LABELS[task.priority as keyof typeof TASK_PRIORITY_LABELS] || task.priority}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-700">{originalDeveloperName}</td>
                      <td className="px-4 py-4 text-gray-700">{dueDate ? formatDate(dueDate) : 'Not set'}</td>
                      <td className="px-4 py-4 text-gray-700">{attemptCount}</td>
                      <td className="px-4 py-4 text-gray-500">{formatRelativeTime(task.updated_at)}</td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => openTaskDetails(task)}
                          >
                            View
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => handlePass(task)}
                            isLoading={workflowMutation.isPending && workflowMutation.variables?.task.id === task.id}
                          >
                            Pass
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="danger"
                            onClick={() => openFailModal(task)}
                            disabled={workflowMutation.isPending}
                          >
                            Fail
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {failModalState && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
          <div className="w-full max-w-2xl rounded-xl border border-stone-300 bg-stone-100 shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Record Test Failure</h2>
                <p className="text-sm text-gray-500">A failure reason is required before this task returns to development.</p>
              </div>
              <button className="text-gray-500 hover:text-gray-700" onClick={closeFailModal}>
                Close
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div className="rounded-lg border border-stone-300 bg-white/80 p-4">
                <p className="text-sm font-medium text-gray-900">{failModalState.task.title}</p>
                <p className="mt-1 text-sm text-gray-600">
                  {failModalState.task.status === 'in_dev_testing' ? 'First-test failure' : 'Second-test failure'} will return this task to the original developer.
                </p>
              </div>

              <RichTextEditor
                label="Failure Reason"
                value={failureReason}
                onChange={setFailureReason}
                placeholder="Describe what failed, how to reproduce it, and what the developer should fix."
                minHeightClassName="min-h-[140px]"
              />

              {failureError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {failureError}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <Button type="button" variant="ghost" onClick={closeFailModal}>
                Cancel
              </Button>
              <Button type="button" variant="danger" onClick={handleFailSubmit} isLoading={workflowMutation.isPending}>
                Submit Failure
              </Button>
            </div>
          </div>
        </div>
      )}

      {viewTask && (
        <TaskViewDialog
          task={viewTask}
          onClose={closeTaskDetails}
          fullScreen
          assigneeLabel={viewTask.assigned_to ? profileDisplayById.get(viewTask.assigned_to) || 'Unknown user' : 'Unassigned'}
          headerSubtitle="Review request details, delivery status, notes, and attachments."
        >
          <section className="rounded-xl border border-stone-300 bg-stone-50 p-5">
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-gray-900">Attachments</h3>
                    <p className="text-sm text-gray-500">Task files and uploads</p>
                  </div>

                  {taskAttachmentsQuery.isLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <LoadingSpinner text="Loading attachments..." />
                    </div>
                  ) : viewAttachments.length === 0 ? (
                    <p className="text-sm text-gray-500">No attachments yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {viewAttachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white/80 p-4 md:flex-row md:items-center md:justify-between"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-gray-900">{attachment.file_name}</p>
                            <p className="text-xs text-gray-500">
                              Added by {attachment.uploader_name} on {formatDateTime(attachment.created_at)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {attachment.mime_type || 'Unknown type'} | {formatFileSize(attachment.file_size_bytes)}
                            </p>
                          </div>
                          <Button type="button" variant="secondary" size="sm" onClick={() => handleOpenAttachment(attachment)}>
                            Open
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
          </section>

          <section className="rounded-xl border border-stone-300 bg-stone-50 p-5 xl:col-span-2">
                  <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Notes</h3>
                      <p className="text-sm text-gray-500">Notes and activity history</p>
                    </div>
                    <div className="grid gap-3 text-sm text-gray-600 md:grid-cols-2">
                      <div>
                        <span className="font-medium text-gray-700">Created:</span> {formatDateTime(viewTask.created_at)}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Updated:</span> {formatDateTime(viewTask.updated_at)}
                      </div>
                    </div>
                  </div>

                  {taskNotesQuery.isLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <LoadingSpinner text="Loading notes..." />
                    </div>
                  ) : viewNotes.length === 0 ? (
                    <p className="text-sm text-gray-500">No notes yet.</p>
                  ) : (
                    <div className="space-y-4">
                      {viewNotes.map((note) => (
                        <div key={note.id} className="rounded-lg border border-gray-200 bg-white/80 p-4">
                          <div className="mb-3">
                            <p className="text-sm font-medium text-gray-900">{note.author_name}</p>
                            <p className="text-xs text-gray-500">{formatDateTime(note.created_at)}</p>
                          </div>
                          <RichTextContent html={note.content} />
                        </div>
                      ))}
                    </div>
                  )}
          </section>
        </TaskViewDialog>
      )}
    </div>
  )
}